import React, { useEffect, useMemo, useState } from 'react';
import './MyPlanPanel.css';
import baseURL from '../../url';

const SHOW_WEBMAIL_ACCESS = false;

function getPercentage(used, limit) {
    if (!limit) {
        return 0;
    }

    const rawPercentage = (used / limit) * 100;
    if (rawPercentage <= 0) {
        return 0;
    }

    return Math.min(100, Math.max(3, Number(rawPercentage.toFixed(2))));
}

function MetricCard({ label, usedLabel, limitLabel, percentage, accent = 'default', helper }) {
    return (
        <article className={`myPlanMetric ${accent}`}>
            <div className='myPlanMetricHeader'>
                <span>{label}</span>
                <strong>{usedLabel}</strong>
            </div>
            {limitLabel ? <p>{limitLabel}</p> : <p>Sin limite</p>}
            {limitLabel ? (
                <div className='myPlanProgress'>
                    <div className='myPlanProgressBar' style={{ width: `${percentage}%` }} />
                </div>
            ) : (
                <div className='myPlanUnlimited'>Este plan no tiene limite fijo.</div>
            )}
            {helper ? <small>{helper}</small> : null}
        </article>
    );
}

export default function MyPlanPanel() {
    const [planData, setPlanData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadPlan = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await fetch(`${baseURL}/planGet.php?ts=${Date.now()}`, {
                cache: 'no-store'
            });
            const data = await response.json();

            if (!response.ok || data.error) {
                throw new Error(data.error || 'No se pudo cargar la informacion del plan.');
            }

            setPlanData(data);
        } catch (loadError) {
            setError(loadError.message || 'No se pudo cargar la informacion del plan.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPlan();
    }, []);

    const metrics = useMemo(() => {
        if (!planData) {
            return null;
        }

        const { plan, usage } = planData;

        return {
            products: {
                usedLabel: `${usage.productsUsed}`,
                limitLabel: plan.productLimit ? `${plan.productLimit} productos incluidos` : null,
                percentage: getPercentage(usage.productsUsed, plan.productLimit),
            },
            images: {
                usedLabel: `${usage.imagesUsed}`,
                limitLabel: plan.imageLimit ? `${plan.imageLimit} imagenes incluidas` : null,
                percentage: getPercentage(usage.imagesUsed, plan.imageLimit),
            },
            storage: {
                usedLabel: usage.storageUsedLabel,
                limitLabel: `${plan.storageLimitLabel} disponibles para imagenes de productos`,
                percentage: getPercentage(usage.storageUsedBytes, plan.storageLimitBytes),
            },
        };
    }, [planData]);

    return (
        <section className='myPlanPanel'>
            <div className='myPlanHeading'>
                <div>
                    <span className='myPlanEyebrow'>Limites de la tienda</span>
                    <h2>Mi plan</h2>
                    <p>Revisa el plan actual y el uso de productos e imagenes.</p>
                </div>
                <div className='myPlanActions'>
                    <button className='myPlanGhostButton' type='button' onClick={loadPlan}>
                        Actualizar
                    </button>
                    {planData?.plan?.upgradeUrl ? (
                        <a href={planData.plan.upgradeUrl} target='_blank' rel='noopener noreferrer' className='myPlanPrimaryButton'>
                            Mejorar plan
                        </a>
                    ) : (
                        <button className='myPlanPrimaryButton disabled' type='button' disabled>
                            Mejorar plan
                        </button>
                    )}
                </div>
            </div>

            {loading ? <div className='myPlanState'>Cargando detalles del plan...</div> : null}
            {!loading && error ? <div className='myPlanState error'>{error}</div> : null}

            {!loading && !error && planData && metrics ? (
                <>
                    <div className='myPlanSummaryRow'>
                        <article className='myPlanSummaryCard'>
                            <span>Plan actual</span>
                            <strong>{planData.plan.name}</strong>
                            <p>
                                {planData.plan.code === 'pro'
                                    ? 'Incluye productos e imagenes ilimitados con hasta 3 GB de almacenamiento.'
                                    : 'Los limites se actualizan automaticamente al eliminar productos o imagenes.'}
                            </p>
                        </article>
                        {SHOW_WEBMAIL_ACCESS ? (
                            <article className='myPlanSummaryCard'>
                                <span>Mail</span>
                                <strong>Acceso webmail</strong>
                                <p>Abre el correo de la tienda directamente desde este subdominio.</p>
                                <a href={planData.webmailUrl} target='_blank' rel='noopener noreferrer' className='myPlanSecondaryButton'>
                                    Abrir webmail
                                </a>
                            </article>
                        ) : null}
                    </div>

                    <div className='myPlanMetricsGrid'>
                        <MetricCard
                            label='Productos'
                            usedLabel={metrics.products.usedLabel}
                            limitLabel={metrics.products.limitLabel}
                            percentage={metrics.products.percentage}
                            accent='products'
                            helper='Al eliminar un producto este numero baja automaticamente.'
                        />
                        <MetricCard
                            label='Imagenes'
                            usedLabel={metrics.images.usedLabel}
                            limitLabel={metrics.images.limitLabel}
                            percentage={metrics.images.percentage}
                            accent='images'
                            helper='Al quitar imagenes de productos tambien baja este conteo.'
                        />
                        <MetricCard
                            label='Almacenamiento de imagenes'
                            usedLabel={metrics.storage.usedLabel}
                            limitLabel={metrics.storage.limitLabel}
                            percentage={metrics.storage.percentage}
                            accent='storage'
                            helper='Este uso mide la carpeta local de imagenes de productos.'
                        />
                    </div>

                    {!planData.plan.upgradeUrl ? (
                        <p className='myPlanHelperText'>
                            El boton de mejorar plan se activara cuando configures la URL de planes en el panel de plataforma.
                        </p>
                    ) : null}
                </>
            ) : null}
        </section>
    );
}
